import * as d3 from 'd3';
import { isAnonymized, getArtifactTypes } from '@/lib/filters';
import { computeKeywordIdf, weightedKeywordJaccard, normalizeKeyword } from '@/lib/keywordSimilarity';

export { computeKeywordIdf, weightedKeywordJaccard };


export const RELATIONSHIP_TYPES = {
  sharedKeywords: {
    color: 'var(--edge-shared-keywords)',
    strokeWidth: 2,
    dashArray: '6,3',
    label: 'Shared Keywords',
  },
  similar: {
    color: '#88c0d0',
    strokeWidth: 1.5,
    dashArray: null,
    label: 'Similar Research Artifacts',
  },
};

/** Every artifact-level field's values, flattened across a paper's artifacts. */
function flattenArtifactField(paper, getValues) {
  const artifacts = paper.contribution?.artifacts || [];
  const values = [];
  artifacts.forEach((artifact) => values.push(...(getValues(artifact) || [])));
  return [...new Set(values)];
}

function summarizePaper(paper, artifactColors) {
  const typeOfArtifact = getArtifactTypes(paper);

  return {
    id: paper._id,
    title: paper.metadata.title,
    year: paper.metadata.year,
    track: paper.metadata?.track,
    anonymized: isAnonymized(paper),
    userAdded: Boolean(paper._userAdded),
    cluster: typeOfArtifact[0] || 'unknown',
    color: artifactColors[typeOfArtifact[0]] || '#88c0d0',
    keywords: paper.contribution?.keywords || [],
    typeOfArtifact,
    researchMethod: flattenArtifactField(paper, (a) => a.research_method?.methods),
    evaluationMethod: flattenArtifactField(paper, (a) => a.evaluation_method),
    dataType: flattenArtifactField(paper, (a) => a.data_collection?.data_type),
    threatsToValidity: flattenArtifactField(paper, (a) => a.threats_to_validity),
  };
}

/** Value at percentile `p` (0-1) of an ascending-sorted numeric array. */
function percentile(sortedValues, p) {
  if (sortedValues.length === 0) return 0;
  return sortedValues[Math.min(sortedValues.length - 1, Math.floor(sortedValues.length * p))];
}

/** Plain (unweighted) Jaccard similarity between two value sets. */
function jaccard(a, b) {
  const setA = new Set(a);
  const setB = new Set(b);
  if (setA.size === 0 && setB.size === 0) return 0;
  let intersection = 0;
  setA.forEach((v) => { if (setB.has(v)) intersection += 1; });
  const union = setA.size + setB.size - intersection;
  return union === 0 ? 0 : intersection / union;
}

const RESEARCH_ARTIFACT_SUBFIELDS = ['typeOfArtifact', 'researchMethod', 'evaluationMethod', 'dataType', 'threatsToValidity'];


function calculateSimilarity(a, b) {
  let product = 1;
  let count = 0;
  RESEARCH_ARTIFACT_SUBFIELDS.forEach((field) => {
    if (a[field].length === 0 || b[field].length === 0) return;
    product *= jaccard(a[field], b[field]);
    count += 1;
  });
  return count === 0 ? 0 : Math.pow(product, 1 / count);
}

export function buildGraphData(papers, artifactColors, options = {}) {
  const {
    keywordPercentile = 0.9,
    showSimilar = true,
    similarityPercentile = 0.9,
    precomputedKeywordLinks = [],
  } = options;

  const summaries = papers.map((p) => summarizePaper(p, artifactColors));
  const nodes = summaries.map(({ id, title, year, track, anonymized, userAdded, typeOfArtifact, cluster, color, keywords }) => ({
    id, title, year, track, anonymized, userAdded, typeOfArtifact, cluster, color, keywords,
  }));

  const links = [];
  const added = new Set();
  const addLink = (source, target, type, weight = 1) => {
    const key = [source, target].sort().join('--');
    const dedupeKey = `${key}:${type}`;
    if (source === target || added.has(dedupeKey)) return;
    added.add(dedupeKey);
    links.push({ source, target, type, weight, ...RELATIONSHIP_TYPES[type] });
  };

  // Shared Keywords edges are gated by an IDF-weighted Jaccard score (rare
  // keywords count for more than common ones like "process mining"), with
  // the cutoff set to the Nth percentile of that score's own distribution
  // across the corpus — rather than a fixed number — so it stays
  // self-calibrating as papers are added or removed instead of needing to
  // be hand-tuned again each time.
  const keywordIdf = computeKeywordIdf(summaries);
  const keywordPairs = [];
  for (let i = 0; i < summaries.length; i += 1) {
    const a = summaries[i];
    if (a.keywords.length === 0) continue;
    const normA = new Set(a.keywords.map(normalizeKeyword));
    for (let j = i + 1; j < summaries.length; j += 1) {
      const b = summaries[j];
      let shared = false;
      for (const k of b.keywords) {
        if (normA.has(normalizeKeyword(k))) { shared = true; break; }
      }
      if (!shared) continue;
      keywordPairs.push({ a, b, score: weightedKeywordJaccard(a.keywords, b.keywords, keywordIdf) });
    }
  }

  // Anonymized papers have no keyword text on the client to compare (see
  // scripts/build-keyword-links.mjs), so their Shared Keywords scores are
  // precomputed at build time from the real keywords and shipped as bare
  // numbers — folded in here so they go through the same percentile cutoff
  // as the live-computed pairs above, rather than a separate threshold.
  const summaryById = new Map(summaries.map((s) => [s.id, s]));
  precomputedKeywordLinks.forEach(({ a: aId, b: bId, score }) => {
    const a = summaryById.get(aId);
    const b = summaryById.get(bId);
    if (a && b) keywordPairs.push({ a, b, score });
  });

  const keywordScores = keywordPairs.map((p) => p.score).sort((x, y) => x - y);
  const keywordThreshold = percentile(keywordScores, keywordPercentile);
  keywordPairs.forEach(({ a, b, score }) => {
    if (score >= keywordThreshold) addLink(a.id, b.id, 'sharedKeywords', score);
  });

  if (showSimilar) {
    // Same self-calibrating approach as Shared Keywords: the cutoff is the
    // Nth percentile of the similarity score's own distribution (over
    // pairs that share *some* similarity, i.e. score > 0) rather than a
    // fixed number, so it stays meaningful in relation to the size and
    // makeup of the corpus instead of needing to be hand-tuned again as
    // papers are added or removed.
    const simPairs = [];
    for (let i = 0; i < summaries.length; i += 1) {
      for (let j = i + 1; j < summaries.length; j += 1) {
        const sim = calculateSimilarity(summaries[i], summaries[j]);
        if (sim > 0) simPairs.push({ a: summaries[i], b: summaries[j], sim });
      }
    }
    const simScores = simPairs.map((p) => p.sim).sort((x, y) => x - y);
    const simThreshold = percentile(simScores, similarityPercentile);
    simPairs.forEach(({ a, b, sim }) => {
      if (sim >= simThreshold) addLink(a.id, b.id, 'similar', sim);
    });
  }

  return { nodes, links };
}

/** Groups nodes by cluster id (their primary type of artifact). */
function computeClusterHulls(nodes, typesOfArtifact) {
  const clusters = new Map();
  typesOfArtifact.forEach((t) => {
    clusters.set(t.id, { id: t.id, name: t.label, color: t.color, nodes: [] });
  });
  nodes.forEach((n) => {
    const cluster = clusters.get(n.cluster);
    if (cluster) cluster.nodes.push(n);
  });
  return Array.from(clusters.values()).filter((c) => c.nodes.length > 0);
}

/** Convex-hull outline (or circle/ellipse for 1-2 nodes) around a cluster's nodes. */
function generateHullPath(clusterNodes, padding = 25) {
  if (clusterNodes.length === 1) {
    const n = clusterNodes[0];
    const r = padding + 15;
    return `M ${n.x - r},${n.y} a ${r},${r} 0 1,0 ${r * 2},0 a ${r},${r} 0 1,0 ${-r * 2},0`;
  }
  if (clusterNodes.length === 2) {
    const [n1, n2] = clusterNodes;
    const midX = (n1.x + n2.x) / 2;
    const midY = (n1.y + n2.y) / 2;
    const dx = n2.x - n1.x;
    const dy = n2.y - n1.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const rx = dist / 2 + padding;
    const ry = padding + 20;
    const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
    return `M ${midX - rx},${midY} a ${rx},${ry} ${angle} 1,0 ${rx * 2},0 a ${rx},${ry} ${angle} 1,0 ${-rx * 2},0`;
  }

  const points = clusterNodes.map((n) => [n.x, n.y]);
  const hull = d3.polygonHull(points);
  if (!hull) return null;

  const expanded = hull.map((point, i) => {
    const prev = hull[(i - 1 + hull.length) % hull.length];
    const next = hull[(i + 1) % hull.length];
    const dx1 = point[0] - prev[0];
    const dy1 = point[1] - prev[1];
    const dx2 = next[0] - point[0];
    const dy2 = next[1] - point[1];
    const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1) || 1;
    const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2) || 1;
    const nx = (-dy1 / len1 - dy2 / len2) / 2;
    const ny = (dx1 / len1 + dx2 / len2) / 2;
    const nlen = Math.sqrt(nx * nx + ny * ny) || 1;
    return [point[0] + (nx / nlen) * padding, point[1] + (ny / nlen) * padding];
  });

  const line = d3.line().x((d) => d[0]).y((d) => d[1]).curve(d3.curveCatmullRomClosed.alpha(0.5));
  return line(expanded);
}

/** Zoom-scale thresholds for semantic zoom, matching
 * examples/process-mining-methods' relationship graph. */
const ZOOM_LEVELS = {
  ABSTRACT: 0.45,
  MINIMAL: 0.7,
  NORMAL: 1.0,
  DETAILED: 1.5,
  FULL: 2.2,
};

function getZoomLevelName(scale) {
  if (scale < ZOOM_LEVELS.ABSTRACT) return 'Abstract';
  if (scale < ZOOM_LEVELS.MINIMAL) return 'Minimal';
  if (scale < ZOOM_LEVELS.DETAILED) return 'Normal';
  if (scale < ZOOM_LEVELS.FULL) return 'Detailed';
  return 'Full Detail';
}

/** Greedily wraps text into up to 3 lines of roughly `maxWidth` px. */
function wrapText(text, maxWidth) {
  const words = text.split(/\s+/);
  const lines = [];
  let currentLine = '';
  const charWidth = 6.5;
  words.forEach((word) => {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (testLine.length * charWidth > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  });
  if (currentLine) lines.push(currentLine);
  return lines.slice(0, 3);
}

export function createRelationshipGraph(
  container,
  { nodes, links },
  {
    typesOfArtifact = [],
    showLabels = true,
    showClusters = true,
    nodeSpacing = 1.5,
    onNodeClick,
    onNodeHover,
    onNodeLeave,
  } = {}
) {
  const width = container.clientWidth || 900;
  const height = container.clientHeight || 600;

  const svg = d3
    .select(container)
    .append('svg')
    .attr('width', width)
    .attr('height', height)
    .attr('viewBox', [0, 0, width, height]);

  const g = svg.append('g');

  // Assigned once the elements they reference exist further down —
  // declared here (undefined, not TDZ-guarded like `const`) so
  // updateSemanticZoom can safely no-op if a zoom event fires before setup
  // finishes, the same guard examples/process-mining-methods uses.
  let hullGroup;
  let link;
  let nodeGroup;
  let nodeRadius;

  // Start slightly zoomed out from identity, matching
  // examples/process-mining-methods' initial framing.
  const initialScale = 0.85;
  let currentZoomScale = initialScale;

  // Zoom level indicator, fixed to the viewport (appended to `svg`, not the
  // pannable/zoomable `g`).
  const zoomIndicator = svg
    .append('g')
    .attr('class', 'zoom-indicator')
    .attr('transform', `translate(${width - 140}, 20)`);
  zoomIndicator
    .append('rect')
    .attr('width', 130)
    .attr('height', 50)
    .attr('rx', 8)
    .attr('fill', 'var(--surface)')
    .attr('stroke', 'var(--border)')
    .attr('opacity', 0.9);
  const zoomLevelText = zoomIndicator
    .append('text')
    .attr('x', 65)
    .attr('y', 20)
    .attr('text-anchor', 'middle')
    .attr('fill', 'var(--text)')
    .attr('font-size', '11px')
    .attr('font-weight', '600')
    .text(getZoomLevelName(initialScale));
  const zoomPercentText = zoomIndicator
    .append('text')
    .attr('x', 65)
    .attr('y', 38)
    .attr('text-anchor', 'middle')
    .attr('fill', 'var(--text-muted)')
    .attr('font-size', '10px')
    .text(`${Math.round(initialScale * 100)}%`);

  // Zoom hint, fixed to the viewport, fades out once the user has zoomed.
  const zoomHint = svg
    .append('g')
    .attr('class', 'zoom-hint')
    .attr('transform', `translate(${width / 2}, ${height - 16})`);
  zoomHint
    .append('text')
    .attr('text-anchor', 'middle')
    .attr('fill', 'var(--text-muted)')
    .attr('font-size', '10px')
    .attr('opacity', 0.7)
    .text('Scroll to zoom · zoom in for more detail');

  /** Elements appear/disappear/change detail as the zoom scale crosses
   * ZOOM_LEVELS thresholds — node/link/hull emphasis fades out zoomed out,
   * and per-node detail cards fade in zoomed in. */
  function updateSemanticZoom(scale) {
    currentZoomScale = scale;
    zoomLevelText.text(getZoomLevelName(scale));
    zoomPercentText.text(`${Math.round(scale * 100)}%`);

    const isAbstract = scale < ZOOM_LEVELS.ABSTRACT;
    const isMinimal = scale >= ZOOM_LEVELS.ABSTRACT && scale < ZOOM_LEVELS.MINIMAL;
    const isDetailed = scale >= ZOOM_LEVELS.DETAILED && scale < ZOOM_LEVELS.FULL;
    const isFull = scale >= ZOOM_LEVELS.FULL;

    zoomHint.transition().duration(300).attr('opacity', scale === initialScale ? 1 : 0);

    if (!nodeGroup || !link || !hullGroup) return;

    nodeGroup
      .select('circle.node-circle')
      .transition()
      .duration(150)
      .attr('r', (d) => {
        const base = nodeRadius(d);
        if (isAbstract) return base * 0.6;
        if (isMinimal) return base * 0.8;
        return base;
      })
      .attr('stroke-width', isAbstract ? 1 : 1.5);

    link
      .transition()
      .duration(150)
      .attr('stroke-opacity', (d) => {
        const base = d.dashArray ? 0.35 : 0.6;
        if (isAbstract) return base * 0.35;
        if (isMinimal) return base * 0.65;
        return base;
      })
      .attr('stroke-width', (d) => {
        if (isAbstract) return d.strokeWidth * 0.5;
        if (isMinimal) return d.strokeWidth * 0.75;
        return d.strokeWidth;
      });

    hullGroup
      .selectAll('.cluster-hull')
      .transition()
      .duration(150)
      .attr('fill-opacity', () => (isAbstract ? 0.15 : isMinimal ? 0.12 : 0.08))
      .attr('stroke-opacity', () => (isAbstract ? 0.5 : isMinimal ? 0.4 : 0.35))
      .attr('stroke-width', () => (isAbstract ? 3 : 2));

    hullGroup
      .selectAll('.cluster-label')
      .transition()
      .duration(150)
      .attr('font-size', () => (isAbstract ? '16px' : isMinimal ? '14px' : '12px'))
      .attr('opacity', () => (isAbstract ? 0.9 : isMinimal ? 0.8 : isFull ? 0.4 : 0.7));

    updateDetailCards(isDetailed, isFull);
  }

  /** Per-node cards shown only once zoomed past DETAILED/FULL — title
   * (wrapped), year/track, and (FULL only) keyword badges + artifact-type badge. */
  function updateDetailCards(showDetailed, showFull) {
    if (!nodeGroup) return;
    nodeGroup.selectAll('.detail-card').remove();
    if (!showDetailed && !showFull) return;

    nodeGroup.each(function eachNode(d) {
      if (d.anonymized) return;

      const el = d3.select(this);
      const r = nodeRadius(d);

      const cardGroup = el.append('g').attr('class', 'detail-card').attr('transform', `translate(${r + 8}, ${-r - 5})`);

      const cardBg = cardGroup
        .append('rect')
        .attr('rx', 6)
        .attr('ry', 6)
        .attr('fill', 'var(--surface)')
        .attr('stroke', d.color)
        .attr('stroke-width', 1.5)
        .attr('opacity', 0.95);

      const content = cardGroup.append('g').attr('transform', 'translate(10, 12)');
      let y = 0;

      const title = d.title || 'Anonymized paper';
      const nameText = showFull ? title : title.length > 28 ? `${title.slice(0, 28)}…` : title;
      const nameLines = wrapText(nameText, showFull ? 200 : 150);
      nameLines.forEach((line, i) => {
        content
          .append('text')
          .attr('y', y + i * 14)
          .attr('fill', 'var(--text)')
          .attr('font-size', '12px')
          .attr('font-weight', '600')
          .text(line);
      });
      y += nameLines.length * 14 + 6;

      const metaText = [d.year, d.track].filter(Boolean).join(' · ');
      if (metaText) {
        content.append('text').attr('y', y).attr('fill', 'var(--text-muted)').attr('font-size', '10px').text(metaText);
        y += 14;
      }

      if (showFull && d.keywords.length > 0) {
        y += 4;
        let x = 0;
        d.keywords.slice(0, 4).forEach((kw) => {
          const badge = content.append('g').attr('transform', `translate(${x}, ${y})`);
          const badgeText = badge
            .append('text')
            .attr('y', 9)
            .attr('fill', d.color)
            .attr('font-size', '9px')
            .attr('font-weight', '500')
            .text(kw);
          const textWidth = badgeText.node().getComputedTextLength();
          badge
            .insert('rect', 'text')
            .attr('x', -4)
            .attr('y', 0)
            .attr('width', textWidth + 8)
            .attr('height', 14)
            .attr('rx', 3)
            .attr('fill', d.color)
            .attr('opacity', 0.15);
          x += textWidth + 12;
        });
        y += 20;
      }

      if (showFull) {
        const artifactLabel = typesOfArtifact.find((t) => t.id === d.cluster)?.label || d.cluster;
        const badge = content.append('g').attr('transform', `translate(0, ${y})`);
        badge.append('circle').attr('cx', 5).attr('cy', 6).attr('r', 4).attr('fill', d.color);
        badge.append('text').attr('x', 12).attr('y', 9).attr('fill', 'var(--text-secondary)').attr('font-size', '10px').text(artifactLabel);
        y += 16;
      }

      const bbox = content.node().getBBox();
      cardBg.attr('x', 0).attr('y', 0).attr('width', bbox.width + 20).attr('height', bbox.height + 16);

      cardGroup.attr('opacity', 0).transition().duration(200).attr('opacity', 1);
    });
  }

  const zoomBehavior = d3
    .zoom()
    .scaleExtent([0.2, 4])
    .on('zoom', (event) => {
      g.attr('transform', event.transform);
      if (Math.abs(event.transform.k - currentZoomScale) > 0.05) {
        updateSemanticZoom(event.transform.k);
      }
    });
  svg.call(zoomBehavior);

  const initialTransform = d3.zoomIdentity
    .translate(width / 2, height / 2)
    .scale(initialScale)
    .translate(-width / 2, -height / 2);
  svg.call(zoomBehavior.transform, initialTransform);

  const degree = {};
  nodes.forEach((n) => { degree[n.id] = 0; });
  links.forEach((l) => {
    degree[l.source] = (degree[l.source] || 0) + 1;
    degree[l.target] = (degree[l.target] || 0) + 1;
  });

  // Cluster centers: one per type of artifact, arranged around a circle so
  // hulls form visually distinct regions instead of one dense blob. Capped
  // so cluster centers stay well inside the canvas — otherwise, on a
  // narrow or short canvas (or at high spacing), a cluster's target center
  // lands past the edge and its nodes clamp into a flat line along the
  // border instead of spreading out.
  const clusterMargin = 150;
  const clusterRadius = Math.min(
    Math.min(width, height) * 0.32 * nodeSpacing,
    Math.max(40, Math.min(width, height) / 2 - clusterMargin)
  );
  const clusterCenters = {};
  typesOfArtifact.forEach((t, i) => {
    const angle = (i / typesOfArtifact.length) * 2 * Math.PI - Math.PI / 2;
    clusterCenters[t.id] = {
      x: width / 2 + Math.cos(angle) * clusterRadius,
      y: height / 2 + Math.sin(angle) * clusterRadius,
    };
  });

  // Charge/collision/link-distance magnitudes are tuned in pixels against
  // an ~860px-tall canvas. On a smaller canvas the same absolute push
  // shoves nodes disproportionately hard toward the (clamped) edges,
  // producing a visible flat "wall" of nodes along the border — so those
  // magnitudes scale down with the canvas rather than staying fixed.
  const sizeScale = Math.max(0.5, Math.min(1.2, Math.min(width, height) / 860));

  // Position is driven primarily by Similar Papers distance and cluster
  // membership. Shared Keywords edges are still computed and drawn, but
  // contribute no pull in the layout — otherwise their sheer volume would
  // dominate positioning over the more meaningful similarity signal.
  const simulation = d3
    .forceSimulation(nodes)
    .force(
      'link',
      d3
        .forceLink(links)
        .id((d) => d.id)
        .distance((l) => (l.type === 'similar' ? 50 : 110) * nodeSpacing * sizeScale)
        .strength((l) => (l.type === 'similar' ? Math.min((l.weight || 1) * 0.3, 0.4) : 0))
    )
    .force('charge', d3.forceManyBody().strength(-60 * nodeSpacing * sizeScale).distanceMax(600 * sizeScale))
    .force('center', d3.forceCenter(width / 2, height / 2))
    .force('collide', d3.forceCollide((d) => (5 + Math.min(degree[d.id] || 0, 12)) * nodeSpacing * sizeScale))
    .force('clusterX', d3.forceX((d) => clusterCenters[d.cluster]?.x ?? width / 2).strength(0.2))
    .force('clusterY', d3.forceY((d) => clusterCenters[d.cluster]?.y ?? height / 2).strength(0.2));

  hullGroup = g.append('g').attr('class', 'hulls');
  const linkGroup = g.append('g');
  const nodeLayer = g.append('g');

  link = linkGroup
    .attr('fill', 'none')
    .selectAll('line')
    .data(links)
    .join('line')
    .attr('stroke', (d) => d.color)
    .attr('stroke-width', (d) => d.strokeWidth)
    .attr('stroke-dasharray', (d) => d.dashArray || null)
    .attr('stroke-opacity', (d) => (d.dashArray ? 0.35 : 0.6));

  nodeRadius = (d) => 4 + Math.min(degree[d.id] || 0, 12) * 0.6;

  nodeGroup = nodeLayer.selectAll('g').data(nodes).join('g').style('cursor', 'pointer');

  // Dashed accent ring around papers added by the visitor — drawn first so
  // it sits behind (and slightly outside) the node's own circle.
  nodeGroup
    .append('circle')
    .attr('class', 'node-user-added-ring')
    .attr('r', (d) => (d.userAdded ? nodeRadius(d) + 4 : 0))
    .attr('fill', 'none')
    .attr('stroke', 'var(--accent)')
    .attr('stroke-width', 1.5)
    .attr('stroke-dasharray', '3,2');

  nodeGroup
    .append('circle')
    .attr('class', 'node-circle')
    .attr('r', nodeRadius)
    .attr('fill', (d) => d.color)
    .attr('fill-opacity', (d) => (d.anonymized ? 0.35 : 1))
    .attr('stroke', (d) => (d.anonymized ? 'var(--bg)' : 'var(--text)'))
    .attr('stroke-width', 1.5);

  // Labels are hidden by default and only shown for the hovered node (when
  // enabled), not for every node at once.
  const label = nodeGroup
    .append('text')
    .text((d) => {
      const title = d.title || 'Anonymized paper';
      return title.length > 28 ? `${title.slice(0, 28)}…` : title;
    })
    .attr('x', (d) => nodeRadius(d) + 2)
    .attr('y', 3)
    .attr('font-size', 9)
    .attr('fill', 'var(--text-muted)')
    .style('display', 'none')
    .style('pointer-events', 'none');

  let labelsEnabled = showLabels;

  nodeGroup
    .call(drag(simulation))
    .on('click', (event, d) => onNodeClick?.(d.id))
    .on('mouseenter', (event, d) => {
      onNodeHover?.({ ...d, degree: degree[d.id] || 0 });
      if (labelsEnabled) label.style('display', (n) => (n.id === d.id ? null : 'none'));
    })
    .on('mouseleave', () => {
      onNodeLeave?.();
      label.style('display', 'none');
    });

  let clustersEnabled = showClusters;

  function updateHulls() {
    if (!clustersEnabled) {
      hullGroup.selectAll('*').remove();
      return;
    }
    const clusters = computeClusterHulls(nodes, typesOfArtifact);

    const hulls = hullGroup.selectAll('.cluster-hull').data(clusters, (d) => d.id);
    hulls.exit().remove();
    hulls
      .enter()
      .append('path')
      .attr('class', 'cluster-hull')
      .attr('fill', (d) => d.color)
      .attr('fill-opacity', 0.08)
      .attr('stroke', (d) => d.color)
      .attr('stroke-opacity', 0.35)
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '5,5')
      .merge(hulls)
      .attr('d', (d) => generateHullPath(d.nodes, 25));

    const clusterLabels = hullGroup.selectAll('.cluster-label').data(clusters, (d) => d.id);
    clusterLabels.exit().remove();
    clusterLabels
      .enter()
      .append('text')
      .attr('class', 'cluster-label')
      .attr('text-anchor', 'middle')
      .attr('font-size', 12)
      .attr('font-weight', 600)
      .attr('opacity', 0.8)
      .merge(clusterLabels)
      .attr('fill', (d) => d.color)
      .attr('x', (d) => d3.mean(d.nodes, (n) => n.x))
      .attr('y', (d) => d3.min(d.nodes, (n) => n.y) - 32)
      .text((d) => d.name);
  }

  // Keep every node inside the (clipped) canvas — strong link forces like
  // Similar Papers can otherwise pull outlier nodes past the edge, where
  // they're invisible and unclickable.
  const pad = 20;
  simulation.on('tick', () => {
    nodes.forEach((d) => {
      d.x = Math.max(pad, Math.min(width - pad, d.x));
      d.y = Math.max(pad, Math.min(height - pad, d.y));
    });
    link
      .attr('x1', (d) => d.source.x)
      .attr('y1', (d) => d.source.y)
      .attr('x2', (d) => d.target.x)
      .attr('y2', (d) => d.target.y);
    nodeGroup.attr('transform', (d) => `translate(${d.x},${d.y})`);
    updateHulls();
  });

  function drag(sim) {
    function dragstarted(event) {
      if (!event.active) sim.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    }
    function dragged(event) {
      event.subject.fx = event.x;
      event.subject.fy = event.y;
    }
    function dragended(event) {
      if (!event.active) sim.alphaTarget(0);
      event.subject.fx = null;
      event.subject.fy = null;
    }
    return d3.drag().on('start', dragstarted).on('drag', dragged).on('end', dragended);
  }

  return {
    zoomIn: () => svg.transition().duration(200).call(zoomBehavior.scaleBy, 1.4),
    zoomOut: () => svg.transition().duration(200).call(zoomBehavior.scaleBy, 1 / 1.4),
    zoomToFit: () => svg.transition().duration(300).call(zoomBehavior.transform, initialTransform),
    resetSimulation: () => {
      nodes.forEach((n) => { n.fx = null; n.fy = null; });
      simulation.alpha(1).restart();
    },
    setShowLabels: (value) => {
      labelsEnabled = value;
      if (!value) label.style('display', 'none');
    },
    setShowClusters: (value) => {
      clustersEnabled = value;
      updateHulls();
    },
    destroy: () => {
      simulation.stop();
      svg.remove();
    },
  };
}
